import { useState, useCallback, useEffect } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  CrewBenefitsSearchForm,
  BenefitsTable,
  useBenefits,
  getErrorDisplayInfo,
} from '@/features/crew-benefits';
import type { BenefitsSearchRequest, BenefitSortField, SortOrder } from '@/features/crew-benefits';
import { toast } from '@/shared/utils/toast';
import { InfoAlert } from '@/shared/components/alerts';
import { Card } from '@/shared/components/ui/Card';

export const Route = createFileRoute('/_auth/profiles/benefits/search')({
  component: BenefitsPage,
});

/**
 * Crew Benefits Search Page
 * Search and review crew benefits applicants
 */
function BenefitsPage() {
  const [searchParams, setSearchParams] = useState<BenefitsSearchRequest>({
    page: 1,
    limit: 10,
  });

  const [sortBy, setSortBy] = useState<BenefitSortField | undefined>();
  const [sortOrder, setSortOrder] = useState<SortOrder>('ASC');
  const [hasSearched, setHasSearched] = useState(false);

  const { data, isLoading, isError, error } = useBenefits(searchParams, hasSearched);

  const handleSearch = useCallback((params: BenefitsSearchRequest) => {
    setSearchParams({
      ...params,
      page: 1,
      limit: 10,
    });
    setHasSearched(true);
  }, []);

  const handleReset = useCallback(() => {
    setSearchParams({ page: 1, limit: 10 });
    setSortBy(undefined);
    setSortOrder('ASC');
    setHasSearched(false);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setSearchParams((prev) => ({ ...prev, page }));
  }, []);

  const handleSort = useCallback(
    (field: BenefitSortField) => {
      const newOrder = sortBy === field && sortOrder === 'ASC' ? 'DESC' : 'ASC';
      setSortBy(field);
      setSortOrder(newOrder);
      setSearchParams((prev) => ({
        ...prev,
        sort_by: field,
        order: newOrder,
        page: 1,
      }));
    },
    [sortBy, sortOrder]
  );

  useEffect(() => {
    if (hasSearched && isError && error) {
      const errorInfo = getErrorDisplayInfo(error);
      if (errorInfo.variant === 'warning') {
        toast.warning({ title: errorInfo.title, description: errorInfo.message });
      } else {
        toast.error({ title: errorInfo.title, description: errorInfo.message });
      }
    }
  }, [isError, error, hasSearched]);

  return (
    <div className="container mx-auto p-6">
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Left Sidebar - Search Form */}
        <div className="lg:w-80 shrink-0">
          <CrewBenefitsSearchForm
            onSearch={handleSearch}
            onReset={handleReset}
            isLoading={isLoading}
          />
        </div>

        {/* Right Main Content - Results */}
        <div className="flex-1 min-w-0">
          {hasSearched ? (
            <div className="space-y-4">
              <div className="overflow-x-auto w-full">
                <BenefitsTable
                  benefits={data?.list ?? []}
                  isLoading={isLoading}
                  currentPage={data?.page.currentPage ?? 1}
                  totalPages={data?.page.totalPages ?? 1}
                  totalRecords={data?.page.totalRecords ?? 0}
                  pageSize={data?.page.limit ?? 10}
                  onPageChange={handlePageChange}
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSort={handleSort}
                />
              </div>
              {data && data.list.length > 0 && (
                <div className="text-sm text-gray-500 text-center">
                  Page {data.page.currentPage} of {data.page.totalPages}
                </div>
              )}
            </div>
          ) : (
            <Card className="flex justify-center py-20 pl-10 pr-10">
              <InfoAlert message="Use the search form panel in the left to find crew benefits applicants." showIcon={true} />
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
